/**
 * Main menu: title screen, solo skirmish setup (faction + bot count), and
 * multiplayer join/lobby flow. Pure DOM, no canvas.
 */

import { BUILDINGS, FACTIONS, PHASE_NAMES, TECHS, UNITS } from '../sim';
import type { BotDifficulty, BuildingType, FactionId, GameConfig, PlayerConfig, TechId, UnitType } from '../sim';
import { ACHIEVEMENTS } from '../shared/achievements';
import { CopilotPanel } from './copilot';
import { FACTION_ART } from './faction-art';
import { GameSession } from './session';
import { SPRITES } from './sprites';

const DIFFICULTIES: BotDifficulty[] = ['easy', 'normal', 'hard'];
const DIFFICULTY_LABELS: Record<BotDifficulty, string> = { easy: 'Easy', normal: 'Normal', hard: 'Hard' };

function difficultySelectHtml(id: string, selected: BotDifficulty): string {
  return `<select id="${id}">
    ${DIFFICULTIES.map((d) => `<option value="${d}" ${d === selected ? 'selected' : ''}>${DIFFICULTY_LABELS[d]}</option>`).join('')}
  </select>`;
}

// `querySelector<HTMLSelectElement>` trips a spurious TS2344 under this
// project's tsconfig ("HTMLSelectElement does not satisfy the constraint
// 'Element'") — @cloudflare/workers-types' HTMLRewriter `Element` global
// clashes with lib.dom's, and HTMLSelectElement is the one DOM element type
// whose overloaded `remove(index?)` doesn't structurally match it. A plain
// cast (rather than the generic call) sidesteps the constraint check.
function qSelect(root: HTMLElement, selector: string): HTMLSelectElement {
  return root.querySelector(selector) as unknown as HTMLSelectElement;
}

export interface MenuHandlers {
  onStartSolo: (config: GameConfig) => void;
  onStartMultiplayer: (session: GameSession) => void;
}

const FACTION_IDS = Object.keys(FACTIONS) as FactionId[];
const UNIT_TYPES = Object.keys(UNITS) as UnitType[];

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.max(0, Math.round(sec % 60));
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Emoji badges for a player's unlocked achievement ids (unknown ids from a
 * newer server version are silently skipped). */
function achievementBadgesHtml(ids: string[]): string {
  return ids
    .map((id) => {
      const def = ACHIEVEMENTS[id];
      if (!def) return '';
      return `<span class="ach-badge" title="${escapeHtml(def.name)} — ${escapeHtml(def.desc)}">${def.emoji}</span>`;
    })
    .join('');
}

/** The full registry as a "what's chaseable" legend for the leaderboard tab. */
function achievementLegendHtml(): string {
  return Object.values(ACHIEVEMENTS)
    .map(
      (def) => `<div class="ach-legend-row">
        <span class="ach-badge">${def.emoji}</span>
        <span class="ach-legend-name">${escapeHtml(def.name)}</span>
        <span class="ach-legend-desc">${escapeHtml(def.desc)}</span>
      </div>`,
    )
    .join('');
}

// --- ranked account (localStorage) -----------------------------------------

const ACCOUNT_KEY = 'tokenomics.account';
/** Last name used to join a multiplayer room. Prefilled on load so a page
 * refresh + Join reclaims the same seat (the room matches rejoins by name,
 * and the room code already survives in the ?room= URL param). */
const MP_NAME_KEY = 'tokenomics.mpName';

function loadMpName(): string {
  try {
    return localStorage.getItem(MP_NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

function saveMpName(name: string): void {
  try {
    localStorage.setItem(MP_NAME_KEY, name);
  } catch {
    // ignore (private browsing / storage full)
  }
}

interface StoredAccount {
  name: string;
  playerId: string;
  secret: string;
}

function loadAccount(): StoredAccount | null {
  try {
    const raw = localStorage.getItem(ACCOUNT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as StoredAccount).name === 'string' &&
      typeof (parsed as StoredAccount).playerId === 'string' &&
      typeof (parsed as StoredAccount).secret === 'string'
    ) {
      return parsed as StoredAccount;
    }
  } catch {
    // malformed/private-mode localStorage: treat as no account
  }
  return null;
}

function saveAccount(account: StoredAccount): void {
  try {
    localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
  } catch {
    // ignore (private browsing / storage full): ranked play just won't persist
  }
}

function forgetAccount(): void {
  try {
    localStorage.removeItem(ACCOUNT_KEY);
  } catch {
    // ignore
  }
}

/** 32 random bytes as hex (64 chars) — well over the 16-char minimum. */
function generateSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// --- leaderboard fetch shapes (mirrors GET /api/leaderboard's response) ----

interface LeaderboardPlayer {
  name: string;
  rating: number;
  games: number;
  wins: number;
  losses: number;
  bestNet: number;
  fastestWinSec: number | null;
  /** unlocked achievement ids, in registry order (absent on old responses) */
  achievements?: string[];
}

interface RecentMatchParticipant {
  name: string;
  playerId: string | null;
  faction: string;
  isBot: boolean;
  won: boolean;
}

interface RecentMatch {
  room: string;
  playedAt: number;
  durationSec: number;
  winnerName: string | null;
  participants: RecentMatchParticipant[];
}

interface LeaderboardResponse {
  players: LeaderboardPlayer[];
  recentMatches: RecentMatch[];
  updatedAt: number;
}

/** The single faction-exclusive unit for a faction (UNITS[t].faction), if any. */
function uniqueUnitName(fid: FactionId): string | null {
  const t = UNIT_TYPES.find((ut) => UNITS[ut].faction === fid);
  return t ? UNITS[t].name : null;
}

function factionCardsHtml(selected: FactionId, idPrefix: string): string {
  return FACTION_IDS.map((fid) => {
    const f = FACTIONS[fid];
    const unique = uniqueUnitName(fid);
    return `<div class="faction-card ${fid === selected ? 'selected' : ''}" data-faction="${fid}" data-group="${idPrefix}">
      <img class="faction-art" src="${FACTION_ART[fid]}" alt="" loading="lazy" />
      <div class="faction-body">
        <div class="faction-name">${f.name}</div>
        <div class="faction-tagline">${f.tagline}</div>
        <div class="faction-desc">${f.desc}</div>
        ${unique ? `<div class="faction-unique">Unique unit: ${unique}</div>` : ''}
      </div>
    </div>`;
  }).join('');
}

// --- Field Guide (Codex) content ------------------------------------------
// Hand-written identity/lore per house (the parts a manual should carry:
// who they are, the tradeoff, where they start). The cost-profile strip is
// DERIVED from the real faction multipliers below so it never drifts.
const FACTION_GUIDE: Record<FactionId, {
  accent: string;
  role: string;
  background: string;
  playsLike: string;
  opensWith: string;
}> = {
  hyperscale: {
    accent: '#4f8ef7',
    role: 'The Incumbent',
    background:
      "The company that already runs the world's compute. Deep capital, vast capacity, and a monthly invoice with a comma you didn't plan for. Titans win by out-provisioning the room — but every idle rack is still on the meter.",
    playsLike: 'Deep pockets and raw capacity, paid for with a hotter bill and a slower shipyard.',
    opensWith: 'The biggest war chest — you can afford to build first and ask questions later.',
  },
  serverless: {
    accent: '#eab308',
    role: 'The Sprinter',
    background:
      'No fleet to idle. You pay per invocation and scale to zero the instant demand dies. Lean, fast, and spiky — the Guild wants to win the match before the meter ever catches up.',
    playsLike: 'Pure pay-as-you-go tempo: cheapest to run and quickest to build, with thinner margins the longer a game drags.',
    opensWith: 'The leanest footprint and the fastest clock — no GPU clusters to raise; serves just bill per call.',
  },
  openweights: {
    accent: '#46a758',
    role: 'The Commons',
    background:
      'Self-hosted models and a community that tunes them in the open. Serving is cheap and data flows fast, but revenue per call is thinner and the premium units want extra data. The Collective wins by numbers and cheap iteration.',
    playsLike: 'Cheap serving and a fast data pipeline, traded against thinner revenue and data-taxed premium units.',
    opensWith: 'A community data pipeline — your engineers harvest far faster from the first minute.',
  },
};

/** All faction-exclusive units for a faction, phase-ordered (its signature
 *  early unit, then its Run-phase apex). */
function factionUnits(fid: FactionId): UnitType[] {
  return UNIT_TYPES.filter((t) => UNITS[t].faction === fid).sort((a, b) => UNITS[a].phase - UNITS[b].phase);
}

/** A short phase badge — only for units/buildings that unlock past Crawl, so
 *  it reads as "there's more waiting up the ladder" (a reason to advance). */
function phaseBadge(phase: number): string {
  return phase > 0 ? `<span class="codex-phase-tag phase-${phase}">${PHASE_NAMES[phase]}</span>` : '';
}

/** One catalog row: a sprite slot (hydrated after mount), name + phase badge,
 *  and the entity's own flavor text. */
function catalogRow(kind: 'unit' | 'building', type: string, name: string, desc: string, phase: number): string {
  return `<div class="codex-cat">
      <span class="codex-sprite" data-kind="${kind}" data-type="${type}"></span>
      <div class="codex-cat-text">
        <span class="codex-cat-name">${name}${phaseBadge(phase)}</span>
        <span class="codex-cat-desc">${escapeHtml(desc)}</span>
      </div>
    </div>`;
}
const unitRow = (t: UnitType) => catalogRow('unit', t, UNITS[t].name, UNITS[t].desc, UNITS[t].phase);
const buildingRow = (b: BuildingType) => catalogRow('building', b, BUILDINGS[b].name, BUILDINGS[b].desc, BUILDINGS[b].phase);

// Research, sorted into the real FinOps phases (Inform → Optimize → Operate).
// The tech's own desc IS its effect, so the manual explains the discipline
// without prescribing an order. (Test-Time Compute is a Run combat capstone,
// not a cost practice, so it sits with the units, not here.)
const FINOPS_BUCKETS: { key: string; title: string; blurb: string; techs: TechId[] }[] = [
  {
    key: 'inform',
    title: 'Inform',
    blurb: 'See where the money goes.',
    techs: ['taggingPolicy', 'focusAdoption', 'unitEconomics'],
  },
  {
    key: 'optimize',
    title: 'Optimize',
    blurb: 'Spend less for the same outcome.',
    techs: ['promptCaching', 'modelRouting', 'batchInference', 'speculativeDecoding', 'autoscaling', 'commitmentDiscount1', 'commitmentDiscount2'],
  },
  {
    key: 'operate',
    title: 'Operate',
    blurb: 'Keep it reliable — and secure.',
    techs: ['anomalyDetection', 'inputSanitization', 'rateLimiting', 'dataProvenance'],
  },
];

function techRow(t: TechId): string {
  const s = TECHS[t];
  return `<li class="codex-tech">
      <span class="codex-tech-name">${s.name}${phaseBadge(s.phase)}</span>
      <span class="codex-tech-effect">${escapeHtml(s.desc)}</span>
    </li>`;
}

/** Draw a baked sprite canvas into a fresh, crisply integer-scaled copy
 *  centered in a fixed box, so every catalog icon shares one tile size. */
function scaledSprite(src: HTMLCanvasElement, box: number): HTMLCanvasElement {
  const scale = Math.max(1, Math.floor(box / src.width));
  const w = src.width * scale;
  const h = src.height * scale;
  const out = document.createElement('canvas');
  out.width = box;
  out.height = box;
  const ctx = out.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(src, Math.floor((box - w) / 2), Math.floor((box - h) / 2), w, h);
  return out;
}

/** Fill every `.codex-sprite` placeholder with its rendered sprite. Runs once
 *  after the menu mounts; cheap (a couple dozen small canvases). */
function hydrateCodexSprites(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('.codex-sprite[data-kind]').forEach((slot) => {
    if (slot.dataset.hydrated) return;
    const kind = slot.dataset.kind;
    const type = slot.dataset.type as string;
    try {
      const src =
        kind === 'building'
          ? SPRITES.getBuilding(type as BuildingType, 0, 0)
          : SPRITES.getUnit(type as UnitType, 0, 0);
      slot.appendChild(scaledSprite(src, 52));
      slot.dataset.hydrated = '1';
    } catch {
      // unknown/unbuilt sprite: leave the slot empty rather than crash the menu
    }
  });
}

/** Human-readable "cost profile" bullets derived from a faction's real
 *  multipliers, so the manual matches the sim even after balance tweaks. */
function factionProfile(fid: FactionId): string[] {
  const f = FACTIONS[fid];
  const up = (m: number, label: string) => `+${Math.round((m - 1) * 100)}% ${label}`;
  const down = (m: number, label: string) => `−${Math.round((1 - m) * 100)}% ${label}`;
  const out: string[] = [];
  if (f.tokenless) out.push('No GPU clusters — serves bill per call (pure OpEx)');
  // token-generation mult only matters to factions that actually run on tokens
  if (!f.tokenless && f.tokenGenMult > 1) out.push(up(f.tokenGenMult, 'token generation'));
  if (!f.tokenless && f.tokenGenMult < 1) out.push(down(f.tokenGenMult, 'token generation'));
  if (f.tokenCostMult < 1) out.push(down(f.tokenCostMult, 'token cost per serve'));
  if (f.gatherRateMult && f.gatherRateMult > 1) out.push(up(f.gatherRateMult, 'data gathering'));
  if (f.budgetBonus > 0) out.push(`+$${f.budgetBonus} starting budget`);
  if (f.buildingBurnMult < 1) out.push(down(f.buildingBurnMult, 'building burn'));
  if (f.buildingBurnMult > 1) out.push(up(f.buildingBurnMult, 'building burn'));
  if (f.buildSpeedMult < 1) out.push(`${Math.round((1 - f.buildSpeedMult) * 100)}% faster builds`);
  if (f.buildSpeedMult > 1) out.push(`${Math.round((f.buildSpeedMult - 1) * 100)}% slower builds`);
  if (f.revenueMult < 1) out.push(down(f.revenueMult, 'revenue per serve'));
  if (f.substationPowerOverride) out.push('Substations supply bulk power');
  return out;
}

function codexFactionSpread(fid: FactionId): string {
  const f = FACTIONS[fid];
  const g = FACTION_GUIDE[fid];
  const sigs = factionUnits(fid);
  return `
    <article class="codex-house" style="--house:${g.accent}">
      <div class="codex-house-art"><img src="${FACTION_ART[fid]}" alt="" loading="lazy" /></div>
      <div class="codex-house-body">
        <span class="codex-house-role">${g.role}</span>
        <h3 class="codex-house-name">${f.name}</h3>
        <p class="codex-house-tag">${f.tagline}</p>
        <p class="codex-house-bg">${g.background}</p>
        <dl class="codex-house-facts">
          <div><dt>Plays like</dt><dd>${g.playsLike}</dd></div>
          <div><dt>You open with</dt><dd>${g.opensWith}</dd></div>
        </dl>
        <div class="codex-profile">
          <span class="codex-profile-label">Cost profile</span>
          <ul>${factionProfile(fid).map((p) => `<li>${p}</li>`).join('')}</ul>
        </div>
        <div class="codex-house-units">
          <span class="codex-profile-label">Signature units</span>
          ${sigs.map(unitRow).join('')}
        </div>
      </div>
    </article>`;
}

function codexHtml(): string {
  return `
    <div class="codex">
      <header class="codex-hero">
        <div class="codex-eyebrow">Field Manual</div>
        <h2 class="codex-headline">You don't command an army. You run the bill.</h2>
        <p class="codex-lede">Every match you run the technology economics of an AI lab: harvest data, fuel agents, serve the market, and keep revenue ahead of a cloud bill that never sleeps. What follows is orientation, not a playbook — where each house starts and what it trades away. How you win is yours to find.</p>
      </header>

      <section class="codex-section">
        <div class="codex-kicker">The loop</div>
        <ol class="codex-loop">
          <li><span class="codex-loop-n">1</span><p><b>Harvest.</b> Platform Engineers mine data nodes. Data is the context that keeps your models fresh — serve on an empty pool and customers pay far less.</p></li>
          <li><span class="codex-loop-n">2</span><p><b>Fuel.</b> GPU Clusters generate tokens, the fuel every agent spends. (Serverless skips the clusters and pays per call instead.)</p></li>
          <li><span class="codex-loop-n">3</span><p><b>Serve.</b> Inference Agents work market segments for revenue. Revenue minus burn is the whole game — the meter runs every second.</p></li>
          <li><span class="codex-loop-n">4</span><p><b>Mature.</b> Advance the maturity ladder to unlock optimizations, defenses, and heavier units.</p></li>
        </ol>
        <div class="codex-legend">
          <span class="codex-res"><b class="codex-glyph">$</b> Budget — runway. Zero for 45s and you're insolvent.</span>
          <span class="codex-res"><b class="codex-glyph">&#9889;</b> Tokens — agent fuel, capped by your clusters.</span>
          <span class="codex-res"><b class="codex-glyph">&#9670;</b> Data — context freshness, spent on every serve.</span>
          <span class="codex-res"><b class="codex-glyph">&#128268;</b> Power — capacity. Overdraw and you brown out.</span>
        </div>
      </section>

      <section class="codex-section">
        <div class="codex-kicker">The maturity ladder</div>
        <div class="codex-phases">
          <div class="codex-phase"><b>Crawl</b><span>Get cost-aware. Stand up the economy.</span></div>
          <div class="codex-phase"><b>Walk</b><span>Real practice: optimizations, caches, and your first real defenses.</span></div>
          <div class="codex-phase"><b>Run</b><span>FinOps at scale — this is where your house's apex unit, siege, frontier agents, and the deepest commitments unlock. The reason to push the ladder.</span></div>
        </div>
      </section>

      <section class="codex-section">
        <div class="codex-kicker">The three houses</div>
        <p class="codex-note">Each is a real cloud-cost strategy. Pick the tradeoff you want to live with.</p>
        ${FACTION_IDS.map(codexFactionSpread).join('')}
      </section>

      <section class="codex-section">
        <div class="codex-kicker">The roster</div>
        <p class="codex-note">The agents you'll field. Faction-exclusive units live with their house above.</p>
        <div class="codex-cat-group-label">Economy &amp; scouting</div>
        <div class="codex-cat-grid">
          ${['engineer', 'inferenceAgent', 'scoutCrawler'].map((t) => unitRow(t as UnitType)).join('')}
        </div>
        <div class="codex-cat-group-label">Military</div>
        <div class="codex-cat-grid">
          ${['redTeamAgent', 'guardrailSentinel', 'loadTester', 'frontierAgent'].map((t) => unitRow(t as UnitType)).join('')}
        </div>
      </section>

      <section class="codex-section">
        <div class="codex-kicker">Structures</div>
        <p class="codex-note">What you build, and roughly when. A <span class="codex-phase-tag phase-1">Walk</span> or <span class="codex-phase-tag phase-2">Run</span> tag means it opens further up the maturity ladder.</p>
        <div class="codex-cat-grid">
          ${(Object.keys(BUILDINGS) as BuildingType[])
            .sort((a, b) => BUILDINGS[a].phase - BUILDINGS[b].phase)
            .map(buildingRow)
            .join('')}
        </div>
      </section>

      <section class="codex-section">
        <div class="codex-kicker">Research — the FinOps discipline</div>
        <p class="codex-note">Your tech tree isn't fantasy magic. It's the real practice model this game is built on: <b>Inform → Optimize → Operate</b>. Research it at your FinOps Command Center, Inference Endpoint, and Security Operations Center — the compounding edge that decides a long game.</p>
        <div class="codex-finops">
          ${FINOPS_BUCKETS.map((bucket) => `
            <div class="codex-finops-col">
              <h4>${bucket.title}<span>${bucket.blurb}</span></h4>
              <ul>${bucket.techs.map(techRow).join('')}</ul>
            </div>`).join('')}
        </div>
      </section>

      <section class="codex-section">
        <div class="codex-kicker">Threats in the field</div>
        <p class="codex-note">Reach Walk and rivals can come at you in software, not just on the map.</p>
        <div class="codex-threats">
          <div class="codex-threat"><b>Prompt Injection</b><span>Jailbreaks your serving agents into runaway loops.</span></div>
          <div class="codex-threat"><b>DDoS</b><span>Floods a building offline — no output, no power — for a while.</span></div>
          <div class="codex-threat"><b>Data Poisoning</b><span>Scrubs your stored data and depresses your revenue.</span></div>
        </div>
        <p class="codex-defense">Answer with a <b>Security Operations Center</b> — it halves every incoming attack — plus the security research it unlocks. Run your own offense from an <b>Adversarial Lab</b>.</p>
      </section>

      <footer class="codex-foot">The economy here isn't invented. Budget, tokens, and data; the Crawl → Walk → Run ladder; the unit economics you're chasing — all of it is drawn from real <a href="https://www.finops.org" target="_blank" rel="noreferrer">FinOps Foundation</a> practice for managing cloud and AI spend.</footer>
    </div>`;
}

function wireFactionCards(root: HTMLElement, group: string, onChange: (f: FactionId) => void): void {
  root.querySelectorAll<HTMLElement>(`.faction-card[data-group="${group}"]`).forEach((card) => {
    card.addEventListener('click', () => {
      root.querySelectorAll<HTMLElement>(`.faction-card[data-group="${group}"]`).forEach((c) => c.classList.remove('selected'));
      card.classList.add('selected');
      onChange(card.dataset.faction as FactionId);
    });
  });
}

export function renderMainMenu(root: HTMLElement, handlers: MenuHandlers): void {
  let soloFaction: FactionId = FACTION_IDS[0];
  let botCount = 1;
  let soloDifficulty: BotDifficulty = 'normal';

  root.innerHTML = `
    <div class="menu-shell">
      <div class="menu-title">
        <h1>TOKENOMICS</h1>
        <p class="subtitle">An RTS about running the technology economics of an AI company.
        Build capacity, serve markets, keep revenue above burn — the FinOps maturity model
        (Crawl → Walk → Run) as your tech tree.</p>
      </div>
      <div class="menu-tabs">
        <button class="menu-tab active" data-tab="solo">Solo Skirmish</button>
        <button class="menu-tab" data-tab="mp">Multiplayer</button>
        <button class="menu-tab" data-tab="leaderboard">Leaderboard</button>
        <button class="menu-tab" data-tab="codex">Field Guide</button>
      </div>

      <div class="menu-panel" id="panel-solo">
        <h2>Choose your faction</h2>
        <div class="faction-grid" id="solo-factions">${factionCardsHtml(soloFaction, 'solo')}</div>
        <h2>Opponents</h2>
        <div class="bot-count" id="bot-count">
          ${[1, 2, 3].map((n) => `<button class="bot-count-btn ${n === botCount ? 'selected' : ''}" data-count="${n}">${n} Bot${n > 1 ? 's' : ''}</button>`).join('')}
        </div>
        <h2>Bot difficulty</h2>
        <div class="solo-difficulty-row">${difficultySelectHtml('solo-difficulty', soloDifficulty)}</div>
        <button class="btn-primary btn-large" id="btn-start-solo">Launch Skirmish</button>
      </div>

      <div class="menu-panel" id="panel-mp" hidden>
        <div id="mp-quickmatch" class="qm-panel">
          <h2>Quick Match (1v1 ranked)</h2>
          <p class="qm-hint">Uses the name, faction, and ranked-account settings below.</p>
          <div class="qm-row">
            <button class="btn-primary btn-large" id="btn-quickmatch">Find Match</button>
            <button class="btn-hud" id="btn-qm-cancel" hidden>Cancel</button>
          </div>
          <div class="qm-matched" id="qm-matched" hidden></div>
          <div class="mp-error" id="qm-error"></div>
        </div>
        <div id="mp-join">
          <h2>Join a room</h2>
          <div class="mp-form">
            <label>Name <input type="text" id="mp-name" maxlength="16" placeholder="Practitioner" /></label>
            <label>Room code <input type="text" id="mp-room" maxlength="12" placeholder="ROOMCODE" /></label>
          </div>
          <div class="account-status" id="account-status"></div>
          <h2>Faction</h2>
          <div class="faction-grid" id="mp-factions">${factionCardsHtml(soloFaction, 'mp')}</div>
          <button class="btn-primary btn-large" id="btn-join-room">Join Room</button>
          <div class="mp-error" id="mp-error"></div>
        </div>
        <div id="mp-lobby" hidden>
          <h2>Lobby</h2>
          <div class="qm-matched" id="lobby-matched" hidden></div>
          <table class="lobby-table" id="lobby-table"></table>
          <div class="lobby-bot-controls" id="lobby-bot-controls" hidden>
            <h2>Bots (host only)</h2>
            <div class="bot-controls-row">
              <select id="bot-faction-select">
                <option value="">Random faction</option>
                ${FACTION_IDS.map((fid) => `<option value="${fid}">${FACTIONS[fid].name}</option>`).join('')}
              </select>
              ${difficultySelectHtml('bot-difficulty-select', 'normal')}
              <button class="btn-hud" id="btn-add-bot">+ Add Bot</button>
              <button class="btn-hud" id="btn-remove-bot">Remove Bot</button>
            </div>
          </div>
          <h2>Your faction</h2>
          <div class="faction-grid" id="lobby-factions">${factionCardsHtml(soloFaction, 'lobby')}</div>
          <div class="lobby-actions">
            <label class="ready-toggle"><input type="checkbox" id="lobby-ready" /> Ready</label>
            <button class="btn-primary" id="btn-host-start" hidden>Start Game</button>
            <button class="btn-hud" id="btn-copy-invite" title="Copy an invite link that opens this room">&#128279; Invite Link</button>
            <button class="btn-hud" id="btn-copilot-lobby">&#129302; Copilot</button>
            <button class="btn-hud" id="btn-leave-lobby">Leave</button>
          </div>
          <div class="mp-error" id="lobby-error"></div>
          <div class="lobby-requeue" id="lobby-requeue" hidden>
            <span>Opponent didn't show.</span>
            <button class="btn-hud" id="btn-requeue">Requeue</button>
          </div>
        </div>
      </div>

      <div class="menu-panel" id="panel-leaderboard" hidden>
        <div class="leaderboard-header">
          <h2>Top Practitioners</h2>
          <button class="btn-hud" id="btn-refresh-leaderboard">&#8635; Refresh</button>
        </div>
        <div class="leaderboard-status" id="leaderboard-status"></div>
        <table class="leaderboard-table" id="leaderboard-table" hidden></table>
        <h2>Recent Matches</h2>
        <div class="recent-matches" id="recent-matches"></div>
        <h2>Achievements</h2>
        <div class="ach-legend">${achievementLegendHtml()}</div>
      </div>

      <div class="menu-panel" id="panel-codex" hidden>${codexHtml()}</div>
    </div>
  `;

  // --- tabs ---
  const tabs = root.querySelectorAll<HTMLButtonElement>('.menu-tab');
  const panelSolo = root.querySelector<HTMLElement>('#panel-solo')!;
  const panelMp = root.querySelector<HTMLElement>('#panel-mp')!;
  const panelLeaderboard = root.querySelector<HTMLElement>('#panel-leaderboard')!;
  const panelCodex = root.querySelector<HTMLElement>('#panel-codex')!;
  // Fill the Field Guide's sprite placeholders once (cheap; a couple dozen
  // small canvases). Deferred a tick so the first paint isn't blocked.
  setTimeout(() => hydrateCodexSprites(panelCodex), 0);
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      tabs.forEach((t) => t.classList.remove('active'));
      tab.classList.add('active');
      const which = tab.dataset.tab;
      panelSolo.hidden = which !== 'solo';
      panelMp.hidden = which !== 'mp';
      panelLeaderboard.hidden = which !== 'leaderboard';
      panelCodex.hidden = which !== 'codex';
      if (which === 'leaderboard') void loadLeaderboard();
    });
  });

  // --- leaderboard ---
  const leaderboardStatusEl = root.querySelector<HTMLElement>('#leaderboard-status')!;
  const leaderboardTableEl = root.querySelector<HTMLTableElement>('#leaderboard-table')!;
  const recentMatchesEl = root.querySelector<HTMLElement>('#recent-matches')!;
  const btnRefreshLeaderboard = root.querySelector<HTMLButtonElement>('#btn-refresh-leaderboard')!;
  let leaderboardLoading = false;

  function renderLeaderboard(body: LeaderboardResponse): void {
    if (body.players.length === 0) {
      leaderboardTableEl.hidden = true;
      leaderboardStatusEl.textContent = 'No ranked games yet — win a multiplayer match to appear here.';
    } else {
      leaderboardTableEl.hidden = false;
      leaderboardStatusEl.textContent = `Updated ${new Date(body.updatedAt).toLocaleTimeString()}`;
      leaderboardTableEl.innerHTML =
        '<tr><th>#</th><th>Name</th><th>Rating</th><th>W-L</th><th>Best $/s</th><th>Fastest win</th><th>Achievements</th></tr>' +
        body.players
          .map(
            (p, i) => `<tr>
        <td>${i + 1}</td>
        <td>${escapeHtml(p.name)}</td>
        <td>${Math.round(p.rating)}</td>
        <td>${p.wins}-${p.losses}</td>
        <td>${p.bestNet.toFixed(1)}</td>
        <td>${p.fastestWinSec != null ? formatDuration(p.fastestWinSec) : '—'}</td>
        <td class="ach-cell">${achievementBadgesHtml(p.achievements ?? [])}</td>
      </tr>`,
          )
          .join('');
    }

    recentMatchesEl.innerHTML =
      body.recentMatches.length === 0
        ? '<div class="recent-match-empty">No matches recorded yet.</div>'
        : body.recentMatches
            .map((m) => {
              const names = m.participants
                .map((p) => `${escapeHtml(p.name)}${p.won ? ' &#127942;' : ''}${p.isBot ? ' (Bot)' : ''}`)
                .join(', ');
              return `<div class="recent-match-row">
                <span class="recent-match-room">${escapeHtml(m.room)}</span>
                <span class="recent-match-names">${names}</span>
                <span class="recent-match-duration">${formatDuration(m.durationSec)}</span>
              </div>`;
            })
            .join('');
  }

  async function loadLeaderboard(): Promise<void> {
    if (leaderboardLoading) return;
    leaderboardLoading = true;
    leaderboardStatusEl.textContent = 'Loading…';
    leaderboardTableEl.hidden = true;
    recentMatchesEl.innerHTML = '';
    try {
      const resp = await fetch('/api/leaderboard?limit=20');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const body = (await resp.json()) as LeaderboardResponse;
      renderLeaderboard(body);
    } catch {
      leaderboardStatusEl.textContent = 'Could not load the leaderboard. Try again.';
    } finally {
      leaderboardLoading = false;
    }
  }

  btnRefreshLeaderboard.addEventListener('click', () => void loadLeaderboard());

  // --- invite deep link: /?room=CODE opens the multiplayer tab prefilled ---
  const inviteRoom = new URLSearchParams(window.location.search).get('room');
  if (inviteRoom) {
    const roomInput = root.querySelector<HTMLInputElement>('#mp-room')!;
    roomInput.value = inviteRoom.toUpperCase().slice(0, 12);
    tabs.forEach((t) => t.classList.toggle('active', t.dataset.tab === 'mp'));
    panelSolo.hidden = true;
    panelMp.hidden = false;
    root.querySelector<HTMLInputElement>('#mp-name')?.focus();
  }

  // --- solo setup ---
  wireFactionCards(root, 'solo', (f) => (soloFaction = f));
  root.querySelectorAll<HTMLButtonElement>('#bot-count .bot-count-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      botCount = Number(btn.dataset.count);
      root.querySelectorAll('#bot-count .bot-count-btn').forEach((b) => b.classList.remove('selected'));
      btn.classList.add('selected');
    });
  });
  qSelect(root, '#solo-difficulty').addEventListener('change', (ev) => {
    soloDifficulty = (ev.target as HTMLSelectElement).value as BotDifficulty;
  });
  root.querySelector<HTMLButtonElement>('#btn-start-solo')!.addEventListener('click', () => {
    const others = FACTION_IDS.filter((f) => f !== soloFaction);
    const players: PlayerConfig[] = [{ name: 'You', faction: soloFaction, isBot: false }];
    for (let i = 0; i < botCount; i++) {
      players.push({
        name: `Bot ${i + 1}`,
        faction: others[i % others.length],
        isBot: true,
        difficulty: soloDifficulty,
      });
    }
    const config: GameConfig = { seed: Math.floor(Math.random() * 2 ** 31), players };
    handlers.onStartSolo(config);
  });

  // --- multiplayer ---
  let mpFaction: FactionId = soloFaction;
  wireFactionCards(root, 'mp', (f) => (mpFaction = f));

  const mpJoin = root.querySelector<HTMLElement>('#mp-join')!;
  const mpLobby = root.querySelector<HTMLElement>('#mp-lobby')!;
  const mpError = root.querySelector<HTMLElement>('#mp-error')!;
  const lobbyError = root.querySelector<HTMLElement>('#lobby-error')!;
  const lobbyTable = root.querySelector<HTMLElement>('#lobby-table')!;
  const readyToggle = root.querySelector<HTMLInputElement>('#lobby-ready')!;
  const hostStartBtn = root.querySelector<HTMLButtonElement>('#btn-host-start')!;
  const botControls = root.querySelector<HTMLElement>('#lobby-bot-controls')!;
  const botFactionSelect = qSelect(root, '#bot-faction-select');
  const botDifficultySelect = qSelect(root, '#bot-difficulty-select');
  const btnAddBot = root.querySelector<HTMLButtonElement>('#btn-add-bot')!;
  const btnRemoveBot = root.querySelector<HTMLButtonElement>('#btn-remove-bot')!;
  wireFactionCards(root, 'lobby', (f) => {
    mpFaction = f;
    session?.setFaction(f);
  });

  let session: GameSession | null = null;
  const copilotPanel = new CopilotPanel(root, () => session);

  // --- quick match (1v1 ranked queue) ---
  const qmPanel = root.querySelector<HTMLElement>('#mp-quickmatch')!;
  const qmBtn = root.querySelector<HTMLButtonElement>('#btn-quickmatch')!;
  const qmCancelBtn = root.querySelector<HTMLButtonElement>('#btn-qm-cancel')!;
  const qmMatchedEl = root.querySelector<HTMLElement>('#qm-matched')!;
  const qmErrorEl = root.querySelector<HTMLElement>('#qm-error')!;
  const btnJoinRoom = root.querySelector<HTMLButtonElement>('#btn-join-room')!;
  const mpRoomInput = root.querySelector<HTMLInputElement>('#mp-room')!;
  const requeueBanner = root.querySelector<HTMLElement>('#lobby-requeue')!;
  const btnRequeue = root.querySelector<HTMLButtonElement>('#btn-requeue')!;
  const lobbyMatchedEl = root.querySelector<HTMLElement>('#lobby-matched')!;

  let qmSocket: WebSocket | null = null;
  let qmQueuedAt: number | null = null;
  let qmQueueSize = 1;
  let qmTickTimer: ReturnType<typeof setInterval> | null = null;

  /** Set once a quick-match pairing hands off a room code, so the lobby flow
   * knows to auto-ready/auto-start instead of waiting on manual clicks — see
   * renderLobby() and the opponent-timeout handling below. */
  let autoMatch = false;
  let autoReadySent = false;
  let autoStartTimer: ReturnType<typeof setTimeout> | null = null;
  let autoStartFired = false;
  let opponentTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  const OPPONENT_TIMEOUT_MS = 25_000;
  const AUTO_START_DELAY_MS = 1000;

  function qmSetSearching(searching: boolean): void {
    qmBtn.disabled = searching;
    qmCancelBtn.hidden = !searching;
    // The manual join form shares the name/faction inputs with Quick Match,
    // but joining a second room while queued would be confusing — disable
    // just the room-code input + Join button, not the shared fields.
    btnJoinRoom.disabled = searching;
    mpRoomInput.disabled = searching;
    if (!searching) qmBtn.textContent = 'Find Match';
  }

  function qmUpdateButtonText(): void {
    if (qmQueuedAt == null) return;
    const waited = Math.max(0, Math.floor((Date.now() - qmQueuedAt) / 1000));
    const m = Math.floor(waited / 60);
    const s = waited % 60;
    qmBtn.textContent = `Searching… ${m}:${s.toString().padStart(2, '0')} · ${qmQueueSize} in queue`;
  }

  function qmStopTicking(): void {
    if (qmTickTimer !== null) {
      clearInterval(qmTickTimer);
      qmTickTimer = null;
    }
  }

  function qmReset(): void {
    qmSetSearching(false);
    qmQueuedAt = null;
    qmStopTicking();
    qmSocket = null;
  }

  function qmCancel(): void {
    try {
      qmSocket?.send(JSON.stringify({ type: 'cancel' }));
      qmSocket?.close();
    } catch {
      // ignore — socket may already be closed
    }
    qmReset();
  }

  function qmHandleMatched(msg: { room?: unknown; opponent?: { name?: unknown; rating?: unknown } }): void {
    // Null out qmSocket first: the server closes this socket right after a
    // match, and we don't want that close event's handler mistaking this
    // expected close for a dropped connection.
    qmSocket = null;
    qmReset();
    qmErrorEl.textContent = '';

    const room = typeof msg.room === 'string' ? msg.room : '';
    const opponentName = typeof msg.opponent?.name === 'string' ? msg.opponent.name : 'opponent';
    const opponentRating = typeof msg.opponent?.rating === 'number' ? Math.round(msg.opponent.rating) : null;
    if (!room) {
      qmErrorEl.textContent = 'Matchmaking returned an invalid room — try again.';
      return;
    }

    const matchedLabel = `Matched vs ${opponentName}${opponentRating != null ? ` (${opponentRating})` : ''}!`;
    qmMatchedEl.hidden = false;
    qmMatchedEl.textContent = `${matchedLabel} Joining room ${room}…`;
    // This copy survives into the lobby view (qmPanel itself gets hidden by
    // joinRoom() below, once the lobby is shown), so the "you got matched"
    // notice is still visible after the auto-join lands.
    lobbyMatchedEl.textContent = matchedLabel;
    lobbyMatchedEl.hidden = false;

    autoMatch = true;
    autoReadySent = false;
    autoStartFired = false;
    if (autoStartTimer !== null) {
      clearTimeout(autoStartTimer);
      autoStartTimer = null;
    }
    if (opponentTimeoutTimer !== null) clearTimeout(opponentTimeoutTimer);
    opponentTimeoutTimer = setTimeout(() => {
      opponentTimeoutTimer = null;
      if (autoMatch && session?.lobby && session.lobby.slots.length < 2) {
        requeueBanner.hidden = false;
      }
    }, OPPONENT_TIMEOUT_MS);

    mpRoomInput.value = room;
    void joinRoom();
  }

  function qmConnect(): void {
    qmErrorEl.textContent = '';
    qmMatchedEl.hidden = true;
    requeueBanner.hidden = true;
    const name = (mpNameInput.value || 'Practitioner').trim();
    saveMpName(name);

    qmSetSearching(true);
    qmQueuedAt = Date.now();
    qmQueueSize = 1;
    qmUpdateButtonText();
    qmTickTimer = setInterval(qmUpdateButtonText, 1000);

    void (async () => {
      const resolved = await resolveAccountClaim(name, qmErrorEl);

      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${proto}://${location.host}/api/matchmake/ws`);
      qmSocket = ws;

      ws.addEventListener('open', () => {
        ws.send(
          JSON.stringify({
            type: 'queue',
            name,
            faction: mpFaction,
            playerId: resolved?.playerId,
            secret: resolved?.secret,
          }),
        );
      });
      ws.addEventListener('message', (ev) => {
        let msg: unknown;
        try {
          msg = JSON.parse(String(ev.data));
        } catch {
          return;
        }
        if (!msg || typeof msg !== 'object' || typeof (msg as { type?: unknown }).type !== 'string') return;
        const parsed = msg as { type: string; [k: string]: unknown };
        if (parsed.type === 'status') {
          if (typeof parsed.queueSize === 'number') qmQueueSize = parsed.queueSize;
          qmUpdateButtonText();
        } else if (parsed.type === 'matched') {
          qmHandleMatched(parsed as { room?: unknown; opponent?: { name?: unknown; rating?: unknown } });
        } else if (parsed.type === 'error' && typeof parsed.message === 'string') {
          qmErrorEl.textContent = parsed.message;
          qmReset();
        }
      });
      ws.addEventListener('close', () => {
        if (qmSocket !== ws) return; // already handled (matched, or a fresh queue started)
        qmErrorEl.textContent = 'Matchmaking connection lost.';
        qmReset();
      });
      ws.addEventListener('error', () => {
        if (qmSocket !== ws) return;
        qmErrorEl.textContent = 'Could not reach the matchmaking service.';
        qmReset();
      });
    })();
  }

  qmBtn.addEventListener('click', () => qmConnect());
  qmCancelBtn.addEventListener('click', () => qmCancel());

  /** Clears all auto-match bookkeeping (auto-ready/auto-start timers, the
   * opponent-timeout banner) — called whenever the lobby is left, whether
   * by the player's own "Leave" click or a Requeue. */
  function resetAutoMatchState(): void {
    autoMatch = false;
    autoReadySent = false;
    autoStartFired = false;
    if (autoStartTimer !== null) {
      clearTimeout(autoStartTimer);
      autoStartTimer = null;
    }
    if (opponentTimeoutTimer !== null) {
      clearTimeout(opponentTimeoutTimer);
      opponentTimeoutTimer = null;
    }
    requeueBanner.hidden = true;
    lobbyMatchedEl.hidden = true;
  }

  btnRequeue.addEventListener('click', () => {
    resetAutoMatchState();
    session?.destroy();
    session = null;
    mpLobby.hidden = true;
    mpJoin.hidden = false;
    qmPanel.hidden = false;
    qmConnect();
  });

  // --- ranked account claim UI ---
  const mpNameInput = root.querySelector<HTMLInputElement>('#mp-name')!;
  if (!mpNameInput.value) mpNameInput.value = loadMpName();
  const accountStatusEl = root.querySelector<HTMLElement>('#account-status')!;
  let claimChecked = false;
  let claimTouched = false;

  function renderAccountStatus(): void {
    const typedName = mpNameInput.value.trim();
    const stored = loadAccount();
    const isStoredName = !!stored && typedName.length > 0 && stored.name.toLowerCase() === typedName.toLowerCase();

    if (isStoredName) {
      accountStatusEl.innerHTML = `<span class="account-ranked">Ranked as <strong>${escapeHtml(
        stored!.name,
      )}</strong> &middot; <a href="#" id="account-forget">forget</a></span>`;
      root.querySelector<HTMLAnchorElement>('#account-forget')!.addEventListener('click', (ev) => {
        ev.preventDefault();
        forgetAccount();
        renderAccountStatus();
      });
      return;
    }

    if (!claimTouched) claimChecked = typedName.length >= 3;
    accountStatusEl.innerHTML = `<label class="account-claim"><input type="checkbox" id="account-claim" ${
      claimChecked ? 'checked' : ''
    } /> Claim this handle for ranked play</label>`;
    root.querySelector<HTMLInputElement>('#account-claim')!.addEventListener('change', (ev) => {
      claimTouched = true;
      claimChecked = (ev.target as HTMLInputElement).checked;
    });
  }

  mpNameInput.addEventListener('input', renderAccountStatus);
  renderAccountStatus();

  /**
   * Shared account-claim resolution used by both the manual "Join Room"
   * form and Quick Match: claims/verifies `name` against the ranked-account
   * service (via the same `#account-claim` checkbox / stored-account state
   * the join form already renders), returning the resulting playerId+secret
   * on success or `undefined` to fall back to an unranked/guest flow.
   * Errors are written into `errorEl` so each caller can show them in its
   * own error slot.
   */
  async function resolveAccountClaim(
    name: string,
    errorEl: HTMLElement,
  ): Promise<{ playerId: string; secret: string } | undefined> {
    const stored = loadAccount();
    const isStoredName = !!stored && stored.name.toLowerCase() === name.toLowerCase();
    const claimBox = root.querySelector<HTMLInputElement>('#account-claim');
    const wantsClaim = isStoredName || claimBox?.checked === true;
    if (!wantsClaim) return undefined;

    const secret = isStoredName ? stored!.secret : generateSecret();
    try {
      const resp = await fetch('/api/account', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name, secret }),
      });
      const body = (await resp.json().catch(() => null)) as
        | { ok?: boolean; playerId?: string; name?: string; reason?: string }
        | null;
      if (resp.ok && body?.ok && body.playerId) {
        saveAccount({ name: body.name ?? name, playerId: body.playerId, secret });
        return { playerId: body.playerId, secret };
      }
      if (resp.status === 409) {
        errorEl.textContent = 'That handle is already claimed by someone else — joining unranked.';
        claimChecked = false;
        claimTouched = true;
        renderAccountStatus();
      } else if (body?.reason) {
        errorEl.textContent = body.reason;
      }
    } catch {
      errorEl.textContent = 'Could not reach the ranked-accounts service — joining unranked.';
    }
    return undefined;
  }

  function renderLobby(): void {
    if (!session || !session.lobby) return;
    const { slots, you } = session.lobby;
    lobbyTable.innerHTML =
      '<tr><th>#</th><th>Name</th><th>Faction</th><th>Ready</th><th>Status</th></tr>' +
      slots
        .map((slot, i) => {
          const botBadge = slot.isBot
            ? ` <span class="bot-marker" title="Bot">&#129302;</span>${
                slot.difficulty
                  ? ` <span class="difficulty-badge difficulty-${slot.difficulty}">${DIFFICULTY_LABELS[slot.difficulty]}</span>`
                  : ''
              }`
            : '';
          return `<tr class="${i === you ? 'me' : ''} ${slot.isBot ? 'bot-slot' : ''}">
        <td>${i + 1}${slot.isHost ? ' (host)' : ''}</td>
        <td>${escapeHtml(slot.name || '—')}${botBadge}</td>
        <td>${slot.connected || slot.isBot ? FACTIONS[slot.faction].name : ''}</td>
        <td>${slot.isBot ? 'Bot' : slot.connected ? (slot.ready ? 'Ready' : 'Not ready') : ''}</td>
        <td>${slot.isBot ? 'Bot' : slot.connected ? 'Connected' : 'Empty'}</td>
      </tr>`;
        })
        .join('');
    const mySlot = slots[you];
    const isHost = !!mySlot?.isHost;
    const humanSlots = slots.filter((s) => !s.isBot).length;
    hostStartBtn.hidden = !isHost;
    // Mirrors the server's own start rule (2+ total slots, 1+ human) purely
    // for UX — the server is still the authority and replies with an error
    // if it disagrees.
    hostStartBtn.disabled = !(slots.length >= 2 && humanSlots >= 1);
    hostStartBtn.title = hostStartBtn.disabled ? 'Need at least 2 slots filled, including 1 human.' : '';
    botControls.hidden = !isHost;
    btnAddBot.disabled = slots.length >= 4;
    btnRemoveBot.disabled = !slots.some((s) => s.isBot);
    lobbyError.textContent = session.lastError ?? '';
    copilotPanel.update();

    if (autoMatch) {
      // Opponent showed up: the "didn't show" concern no longer applies.
      if (slots.length >= 2) {
        if (opponentTimeoutTimer !== null) {
          clearTimeout(opponentTimeoutTimer);
          opponentTimeoutTimer = null;
        }
        requeueBanner.hidden = true;
      }

      // Auto-ready this seat exactly once per match.
      if (!autoReadySent && !mySlot?.ready) {
        autoReadySent = true;
        readyToggle.checked = true;
        session.setReady(true);
      }

      // Host auto-starts once both slots are present and ready, after a
      // short delay so the other player's own auto-ready has time to land.
      const allReady = slots.length >= 2 && slots.every((s) => s.isBot || s.ready);
      if (isHost && allReady && !autoStartFired && autoStartTimer === null) {
        autoStartTimer = setTimeout(() => {
          autoStartTimer = null;
          autoStartFired = true;
          session?.hostStart();
        }, AUTO_START_DELAY_MS);
      }
    }
  }

  root.querySelector<HTMLButtonElement>('#btn-join-room')!.addEventListener('click', () => {
    void joinRoom();
  });

  async function joinRoom(): Promise<void> {
    const name = (mpNameInput.value || 'Practitioner').trim();
    const room = (root.querySelector<HTMLInputElement>('#mp-room')!.value || '').trim().toUpperCase();
    if (!room) {
      mpError.textContent = 'Enter a room code.';
      return;
    }
    mpError.textContent = '';
    saveMpName(name);

    const resolved = await resolveAccountClaim(name, mpError);
    const account = resolved ? { secret: resolved.secret } : undefined;

    session = new GameSession();
    session.onLobbyUpdate = () => renderLobby();
    session.onStart = () => {
      if (session) handlers.onStartMultiplayer(session);
    };
    session.connectMultiplayer(room, name, mpFaction, account);
    // reflect the room in the URL so refresh/share keeps the context
    window.history.replaceState(null, '', `?room=${encodeURIComponent(room)}`);
    qmPanel.hidden = true;
    mpJoin.hidden = true;
    mpLobby.hidden = false;
  }

  root.querySelector<HTMLButtonElement>('#btn-copy-invite')!.addEventListener('click', (ev) => {
    const code = session?.roomCode;
    if (!code) return;
    const url = `${window.location.origin}/?room=${encodeURIComponent(code)}`;
    void navigator.clipboard.writeText(url);
    const btn = ev.currentTarget as HTMLButtonElement;
    const prev = btn.textContent;
    btn.textContent = 'Copied!';
    setTimeout(() => (btn.textContent = prev), 1500);
  });

  readyToggle.addEventListener('change', () => session?.setReady(readyToggle.checked));
  hostStartBtn.addEventListener('click', () => session?.hostStart());
  btnAddBot.addEventListener('click', () => {
    const faction = (botFactionSelect.value || undefined) as FactionId | undefined;
    const difficulty = botDifficultySelect.value as BotDifficulty;
    session?.addBot(faction, difficulty);
  });
  btnRemoveBot.addEventListener('click', () => session?.removeBot());
  root.querySelector<HTMLButtonElement>('#btn-copilot-lobby')!.addEventListener('click', () => copilotPanel.open());
  root.querySelector<HTMLButtonElement>('#btn-leave-lobby')!.addEventListener('click', () => {
    resetAutoMatchState();
    session?.destroy();
    session = null;
    mpJoin.hidden = false;
    mpLobby.hidden = true;
    qmPanel.hidden = false;
  });
}
